import { useState, useRef, useCallback, useEffect } from "react";
import { Globe, FileText, AlignLeft, Upload, X, Loader2, ArrowRight, Info, Sparkles } from "lucide-react";
import { Button } from "@/modern/components/ui/button-shadcn";
import { Input } from "@/modern/components/ui/input";
import { Label } from "@/modern/components/ui/label";
import { cn } from "@/modern/utils";
import { useContextForm } from "../../context/ContextFormProvider";
import { OPENAI_KEY_REQUIRED_TITLE } from "@/react/hooks/useHasOpenAIKey";

type SourceTab = "url" | "pdf" | "note";
type AddMode = "suggestions" | SourceTab;

const SOURCE_CARDS: { id: SourceTab; label: string; icon: typeof Globe; description: string }[] = [
  { id: "url",  label: "URL",        icon: Globe,     description: "Scrape one page from the web" },
  { id: "pdf",  label: "PDF",        icon: FileText,  description: "Upload a text-based PDF or .txt" },
  { id: "note", label: "Add a note", icon: AlignLeft, description: "Paste copy or notes you already have" },
];

interface SetupFormProps {
  /** `add-context` — Add Context modal (sources + strategic suggestions). */
  variant?: "create" | "add-context";
  onCancel?: () => void;
  /** Called after strategic suggestions are kicked off (modal can close). */
  onSuggestionsStarted?: () => void;
}

export const SetupForm = ({
  variant = "create",
  onCancel,
  onSuggestionsStarted,
}: SetupFormProps) => {
  const isAddContext = variant === "add-context";
  const {
    handleSetupSubmit,
    handleGenerateSuggestions,
    handleDuplicateAndGenerate,
    isGeneratingSuggestions,
    isLoadingMapUrl,
    boardContextRecord,
    hasOpenAIKey,
    hasContextView,
    currentStep,
    canCancelDraft,
    cancelDraft,
  } = useContextForm();

  const [name, setName] = useState(boardContextRecord?.name ?? "");
  const [activeMode, setActiveMode] = useState<AddMode>(isAddContext && hasContextView ? "suggestions" : "url");
  const [activeTab, setActiveTab] = useState<SourceTab>("url");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [businessDescription, setBusinessDescription] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isSubmitting || currentStep !== "setup") return;
    if (!isAddContext || !hasContextView) {
      if (!isAddContext) setName(boardContextRecord?.name ?? "");
      setWebsiteUrl(boardContextRecord?.websiteUrl ?? "");
      setBusinessDescription(boardContextRecord?.businessDescription ?? "");
      if (boardContextRecord?.websiteUrl) setActiveTab("url");
      else if (boardContextRecord?.businessDescription) setActiveTab("note");
    } else {
      setWebsiteUrl("");
      setBusinessDescription("");
    }
    setFile(null);
  }, [
    boardContextRecord?.name,
    boardContextRecord?.websiteUrl,
    boardContextRecord?.businessDescription,
    currentStep,
    isSubmitting,
    isAddContext,
    hasContextView,
  ]);

  const onSourceTab = (tab: SourceTab) => {
    setActiveMode(tab);
    setActiveTab(tab);
  };

  const hasAnyInput = websiteUrl.trim() || businessDescription.trim() || file;
  const isDisabled = isSubmitting || isLoadingMapUrl || isGeneratingSuggestions || !hasOpenAIKey;

  const canSubmit = (() => {
    if (isDisabled) return false;
    if (isAddContext && activeMode === "suggestions") return true;
    if (!isAddContext && name.trim().length === 0) return false;
    return Boolean(hasAnyInput);
  })();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setIsSubmitting(true);
    try {
      if (isAddContext && activeMode === "suggestions") {
        if (boardContextRecord?.isActive) {
          await handleDuplicateAndGenerate();
        } else {
          await handleGenerateSuggestions();
        }
        onSuggestionsStarted?.();
        return;
      }
      await handleSetupSubmit({
        name: isAddContext ? (boardContextRecord?.name?.trim() ?? "") : name.trim(),
        websiteUrl: websiteUrl.trim(),
        businessDescription: businessDescription.trim(),
        file,
        fileName: file?.name ?? "",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped && (dropped.type === "application/pdf" || dropped.type === "text/plain")) {
      setFile(dropped);
    }
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) setFile(selected);
  };

  const willDuplicate = isAddContext && boardContextRecord?.isActive === true;

  const submitLabel = (() => {
    if (isAddContext && activeMode === "suggestions") {
      return willDuplicate ? "Duplicate & generate" : "Generate Suggestions";
    }
    if (websiteUrl.trim()) return willDuplicate ? "Duplicate & map pages" : "Map & pick pages";
    return isAddContext
      ? (willDuplicate ? "Duplicate & add to context" : "Add to Context")
      : "Generate questions";
  })();

  const showSourceInput = !isAddContext || activeMode !== "suggestions";

  return (
    <div className="flex flex-col h-full min-h-0">
      <form onSubmit={handleSubmit} className="flex flex-col h-full min-h-0">
        <div className="shrink-0 px-8 py-3.5 border-b border-border/40">
          <h2 className="text-base font-semibold text-foreground">
            {isAddContext ? "Add Context" : "Tell us about your organization"}
          </h2>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {isAddContext
              ? "Drop in a website, PDF, note — or generate AI Strategic Suggestions. We'll update your Context document and ask a few quick questions."
              : "Provide one or more sources — we'll read them and build a Context document for your board."}
          </p>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="w-full px-8 py-4 space-y-5">
            {!isAddContext && (
              <div className="space-y-1.5">
                <Label htmlFor="board-context-name" className="text-sm font-medium text-foreground">
                  Context name
                </Label>
                <Input
                  id="board-context-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Acme Onboarding"
                  disabled={isDisabled}
                  autoFocus
                />
              </div>
            )}

            <div className="space-y-3">
              {isAddContext && hasContextView && (
                <button
                  type="button"
                  onClick={() => setActiveMode("suggestions")}
                  disabled={isDisabled}
                  className={cn(
                    "w-full flex items-center gap-4 rounded-xl border px-5 py-4 text-left transition-all duration-150",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    activeMode === "suggestions"
                      ? "border-primary/50 bg-primary/10"
                      : "border-border/60 bg-muted/20 hover:border-border hover:bg-muted/40"
                  )}
                >
                  <div
                    className={cn(
                      "flex h-10 w-10 items-center justify-center rounded-xl shrink-0",
                      activeMode === "suggestions"
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground"
                    )}
                  >
                    <Sparkles className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-foreground">Strategic Suggestions</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Let AI suggest forward-looking Context based on what you already have.
                    </p>
                  </div>
                </button>
              )}

              <div className="grid grid-cols-3 gap-2.5 items-stretch">
                {SOURCE_CARDS.map(({ id, label, icon: Icon, description }) => {
                  const isActive = isAddContext ? activeMode === id : activeTab === id;
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => onSourceTab(id)}
                      disabled={!hasOpenAIKey}
                      className={cn(
                        "flex flex-col items-start gap-2 rounded-xl border px-3 py-2.5 text-left transition-all duration-150 h-full",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                        "disabled:opacity-50 disabled:cursor-not-allowed",
                        isActive
                          ? "border-primary/50 bg-primary/10"
                          : "border-border/60 bg-muted/20 hover:border-border hover:bg-muted/40"
                      )}
                    >
                      <div
                        className={cn(
                          "flex h-7 w-7 items-center justify-center rounded-lg shrink-0",
                          isActive ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                        )}
                      >
                        <Icon className="h-3.5 w-3.5" />
                      </div>
                      <div>
                        <p
                          className={cn(
                            "text-sm font-semibold leading-tight",
                            isActive ? "text-foreground" : "text-foreground/80"
                          )}
                        >
                          {label}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{description}</p>
                      </div>
                    </button>
                  );
                })}
              </div>

              {showSourceInput && (
                <div className="h-[140px]">
                  {activeTab === "url" && (
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-1.5">
                        <label className="text-sm font-medium text-foreground">Source URL</label>
                        <Info className="h-3.5 w-3.5 text-muted-foreground/60 shrink-0" />
                        <span className="text-xs text-muted-foreground">
                          We'll map the site and let you pick which pages to analyse.
                        </span>
                      </div>
                      <Input
                        type="url"
                        value={websiteUrl}
                        onChange={(e) => setWebsiteUrl(e.target.value)}
                        placeholder="https://yourbusiness.com"
                        disabled={isDisabled}
                      />
                    </div>
                  )}

                  {activeTab === "pdf" && activeMode !== "suggestions" && (
                    <div className="h-full">
                      {file ? (
                        <div className="flex items-center gap-3 p-3 border border-border rounded-xl bg-muted/30 h-full">
                          <FileText className="h-5 w-5 text-primary shrink-0" />
                          <span className="text-sm text-foreground flex-1 truncate">{file.name}</span>
                          <button
                            type="button"
                            onClick={() => setFile(null)}
                            disabled={!hasOpenAIKey}
                            aria-label="Remove file"
                            className="text-muted-foreground hover:text-destructive transition-colors disabled:opacity-50 disabled:pointer-events-none"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <div
                          onDragOver={hasOpenAIKey ? (e) => { e.preventDefault(); setIsDragging(true); } : undefined}
                          onDragLeave={hasOpenAIKey ? () => setIsDragging(false) : undefined}
                          onDrop={hasOpenAIKey ? handleFileDrop : undefined}
                          onClick={hasOpenAIKey ? () => fileInputRef.current?.click() : undefined}
                          className={cn(
                            "flex items-center justify-center gap-4 h-full border-2 border-dashed rounded-xl transition-all duration-150",
                            hasOpenAIKey ? "cursor-pointer" : "cursor-not-allowed opacity-50",
                            isDragging ? "border-primary bg-primary/5" : "border-border/50 bg-muted/10",
                            hasOpenAIKey && !isDragging && "hover:border-primary/50 hover:bg-muted/30"
                          )}
                        >
                          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted shrink-0">
                            <Upload className="h-3.5 w-3.5 text-muted-foreground" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-foreground">Drop your file here</p>
                            <p className="text-xs text-muted-foreground mt-0.5">PDF or .txt · click to browse</p>
                          </div>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept=".pdf,.txt"
                            className="hidden"
                            onChange={handleFileChange}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === "note" && activeMode !== "suggestions" && (
                    <textarea
                      value={businessDescription}
                      onChange={(e) => setBusinessDescription(e.target.value)}
                      placeholder={
                        isAddContext
                          ? "Type or paste a short note, observation, or insight about your organization…"
                          : "Describe your organization: services, customers, location, what makes you different…"
                      }
                      disabled={isDisabled}
                      className="flex w-full h-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 resize-none"
                    />
                  )}
                </div>
              )}

              {isAddContext && hasContextView && activeMode === "suggestions" && (
                <div className="flex items-start gap-4 rounded-xl border border-primary/20 bg-primary/[0.04] px-5 py-4">
                  <Sparkles className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-foreground">
                      Get advisor-style suggestions based on your accepted Context.
                    </p>
                    <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
                      AI will read what's already in Context and propose forward-looking statements.
                      Each appears with an <span className="font-bold text-primary">AI SUGGESTED</span> badge —
                      accept, edit, or delete before anything is used downstream.
                    </p>
                  </div>
                </div>
              )}

              {showSourceInput && activeMode !== "suggestions" && (
                <div className="flex flex-wrap gap-2">
                  {activeTab !== "url" && websiteUrl && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                      <Globe className="h-3 w-3" />
                      {websiteUrl}
                    </span>
                  )}
                  {activeTab !== "pdf" && file && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                      <FileText className="h-3 w-3" />
                      {file.name}
                    </span>
                  )}
                  {activeTab !== "note" && businessDescription && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                      <AlignLeft className="h-3 w-3" />
                      Note added
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="shrink-0 flex items-center justify-between gap-4 px-8 py-3 border-t border-border/40">
          <div className="flex items-center gap-3 min-w-0">
            {canCancelDraft && (
              <Button type="button" variant="ghost" onClick={cancelDraft} disabled={isSubmitting} className="shrink-0">
                Cancel
              </Button>
            )}
            {isAddContext && onCancel && (
              <Button type="button" variant="ghost" onClick={onCancel} disabled={isSubmitting} className="shrink-0">
                Cancel
              </Button>
            )}
            {!canSubmit && activeMode !== "suggestions" && (
              <p className="text-xs text-muted-foreground truncate">
                {!isAddContext && !name.trim()
                  ? "Enter a context name to continue."
                  : "Provide at least one source to continue."}
              </p>
            )}
          </div>
          <div className="ml-auto flex flex-wrap items-center justify-end gap-2 shrink-0">
            <Button
              type="submit"
              disabled={!canSubmit}
              title={!hasOpenAIKey ? OPENAI_KEY_REQUIRED_TITLE : undefined}
              className="gap-2"
            >
              {isSubmitting || isLoadingMapUrl || isGeneratingSuggestions ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {isLoadingMapUrl ? "Mapping site…" : isGeneratingSuggestions ? "Generating suggestions…" : "Saving…"}
                </>
              ) : (
                <>
                  {isAddContext && activeMode === "suggestions" && <Sparkles className="h-4 w-4" />}
                  {submitLabel}
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};
